import { FC, useCallback, useMemo, useState } from "react";
import { Box, Card, Stack, Tooltip, Typography } from "@mui/material";
import { AxisLabelsFormatterContextObject } from "highcharts";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useEffectOnceWhen } from "rooks";
import { ResourceDetailsMetricsContentMetricStatsSelect } from "./ResourceDetailsMetricsContentMetricStatsSelect";
import {
  ResourceDetailsMetricKey,
  MetricPeriods,
  MetricsStatsType,
} from "../../../../utils/types/metrics";
import { metricsHeaderCellTooltips } from "../../../../utils/constants/metricsHeaderCellTooltips";
import {
  ResourceMetricTypeObject,
  ResourceType,
} from "@/services/cloudchipr.api";
import { MultiTypeHighcharts } from "@/storybook/c8r-highcharts/MultiTypeHighcharts";
import { HighchartsProvider } from "@/storybook/c8r-highcharts/common/components/HighchartsProvider";
import {
  HighchartsChartDataType,
  TooltipMarkupGenerator,
} from "@/storybook/c8r-highcharts/common/utils/types/common";
import { formatDate } from "@/utils/helpers/date-time/datetime-format";
import {
  colorsByMetricKey,
  metricKeyLabels,
  metricsChartsPeriods,
} from "@/components/pages/common/resource-details/utils/constants/metrics";
import { convertByteToSize } from "@/components/utils/helpers/convertByteToSize";

interface ResourceDetailsMetricsContentMetricCardProps {
  metricKey: ResourceDetailsMetricKey;
  loading: boolean;
  period: MetricPeriods;
  metrics: ResourceMetricTypeObject;
  resourceType: ResourceType;
  singleStats?: MetricsStatsType;
  setPeriod(period: MetricPeriods): void;
}

export const ResourceDetailsMetricsContentMetricCard: FC<
  ResourceDetailsMetricsContentMetricCardProps
> = ({
  metricKey,
  metrics,
  loading,
  period,
  resourceType,
  singleStats,
  setPeriod,
}) => {
  const [metricsStats, setMetricsStats] = useState<MetricsStatsType>(
    singleStats ?? "maximum",
  );

  const frequency = metricsChartsPeriods[period]?.frequency;
  const frequencyValue = metricsChartsPeriods[period]?.value;
  const color = colorsByMetricKey[metricKey];
  const label = metricKeyLabels[metricKey];

  const percentBased = metrics?.[frequency]?.at(0)?.unit === "Percent";

  const chartData = useMemo(() => {
    const defaultData = {
      categories: [],
      series: [{ data: [], name: label, color: color }],
    };

    const dataCount = metrics?.[frequency]?.length ?? 0;

    let metricsData = metrics?.[frequency]?.toSorted(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );

    if (dataCount > frequencyValue) {
      metricsData = metricsData?.slice(dataCount - frequencyValue, dataCount);
    }

    return (
      metricsData?.reduce((acc, metric) => {
        acc.categories.push(formatDate(metric.timestamp, { type: "date" }));

        if (acc?.series?.at(0)?.data) {
          const value = metric[metricsStats];

          acc.series?.at(0)?.data?.push({ y: value });
        }

        return acc;
      }, defaultData as HighchartsChartDataType) ?? defaultData
    );
  }, [metrics, metricsStats, label, frequency, color, frequencyValue]);

  const xAxisLabelsFormatter = useCallback(
    (params: AxisLabelsFormatterContextObject) =>
      formatDate(`${params.value}`, {
        type: frequency === "hourly" ? "time" : "dayMonthName",
      }),
    [frequency],
  );

  const tooltipValueFormatter: TooltipMarkupGenerator = useCallback(
    (point) => {
      const value = +(point.y ?? 0).toFixed(2);

      const valueWithPercent = `${value}%`;

      return `
        <div style="font-size: 13px; color: #666; margin-bottom: 8px;">${point.category}</div>
      <div style="display: flex; flex-direction: column; gap: 8px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="display: flex; align-items: center; font-size: 11px; margin-right: 16px;">
                  <span style="font-weight: 500; font-size: 13px; width:${point.series.name.length * 10}px; max-width: 250px; min-width: 35px; word-break: break-word; white-space: normal; padding-left: 8px; border-left: 3px solid ${point.color}">
                      ${point.series.name}
                  </span>
              </span>
            <span style="font-weight: 500">${percentBased ? valueWithPercent : convertByteToSize(value, 0)}</span>
          </div>
        </div>
      `;
    },
    [percentBased],
  );

  const tooltip = metricsHeaderCellTooltips.get(resourceType)?.[metricKey];

  const metricsExists = !!Object.values(metrics ?? {}).flat().length;
  useEffectOnceWhen(
    () => setPeriod("24h"),
    !chartData?.categories?.length && metricsExists && period !== "24h",
  );

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <Stack
        p={1}
        spacing={1}
        direction="row"
        alignItems="center"
        borderBottom={1}
        borderColor="grey.300"
      >
        <Typography
          variant="body1"
          fontWeight="medium"
          color="text.secondary"
          pl={1}
        >
          {label}
        </Typography>

        {tooltip && (
          <Tooltip arrow placement="top" title={tooltip}>
            <HelpOutlineIcon fontSize="small" color="info" />
          </Tooltip>
        )}

        {!singleStats && (
          <ResourceDetailsMetricsContentMetricStatsSelect
            metricsStats={metricsStats}
            setMetricsStats={setMetricsStats}
          />
        )}
      </Stack>

      <Box p={1} height={180}>
        <HighchartsProvider>
          <MultiTypeHighcharts
            loading={loading ? { showLoader: true } : false}
            chartType="area"
            percentBased={percentBased}
            chartData={chartData}
            xAxisLabelsFormatter={xAxisLabelsFormatter}
            yAxisLabelsFormatter={
              percentBased ? undefined : yAxisLabelsFormatter
            }
            tooltipMarkupGenerator={tooltipValueFormatter}
            noDataMessage={`No ${label} Metrics`}
          />
        </HighchartsProvider>
      </Box>
    </Card>
  );
};

const yAxisLabelsFormatter = (params: AxisLabelsFormatterContextObject) => {
  const converted = convertByteToSize(params.value, 0);

  return `<span style="font-weight: normal">${params.value ? converted : 0}</span>`;
};
